// HUDA PORT — PDP Buy Now + offer block.
//
// Two controls, one shape: both add the CURRENTLY SELECTED variant and the
// CURRENT quantity to the cart over the Ajax API, then leave the page. Buy Now
// lands on the checkout; «تطبيق» lands on /discount/CODE, which applies the
// code and forwards to the same checkout with the discount field already
// filled. Plus tap-to-copy on the code chip.
//
// ── WHAT THIS FILE DOES NOT TOUCH ─────────────────────────────────────────
// The add-to-cart form. hd-product-card.js owns its `submit` listener and
// hd-product.js owns the tier pills, the quantity stepper, the thumbs and the
// tabs. Nothing here adds a listener to the form, writes to the hidden variant
// input, or changes the quantity — it only READS the form, so add-to-cart
// behaves exactly as it did before this branch.
//
// ── WHERE THE VARIANT AND QUANTITY COME FROM ──────────────────────────────
// `new FormData(form)` — the same object hd-product-card.js posts. That is the
// whole mechanism, and it is why there is no variant-tracking code here:
//   · the hidden [data-pdp="variant-id"] input is rewritten in place by
//     hd-product.js:81 on every tier-pill click, so the FormData built at CLICK
//     time already carries the selected variant;
//   · single-variant products render that same input from
//     selected_or_first_available_variant, so they need no special case;
//   · the quantity input is a named field of the form, so the stepper and any
//     typed value ride along for free.
// Reading a snapshot at parse time would have broken both of the first two.
//
// ── FAILURE IS THE THEME'S EXISTING PATTERN, NOT A NEW ONE ────────────────
// An unavailable variant, a sold-out product, a 422, a network error: all of
// them land in `fallback()`, which calls form.submit(). That is
// hd-product-card.js:152-157 verbatim — the theme's one and only add-to-cart
// failure path. The browser does the real POST, Shopify answers with its own
// error for that exact variant, and the shopper reads it on /cart. No error
// element, no toast, no message string is invented here; there is deliberately
// no new error UI in this file at all.
//
// The consequence is stated rather than hidden: a FAILED Buy Now ends on /cart,
// not on /checkout. That is correct — the shopper is told what went wrong
// instead of being pushed into a checkout that would reject the line anyway.
//
// ── WHY NO CART DRAWER, AND NO `sections` PARAMETER ───────────────────────
// hd-product-card.js asks /cart/add.js to re-render the drawer section because
// it stays on the page. Both controls here navigate away, so a drawer rendered
// into a document that is about to be replaced is a wasted round trip on the
// slowest step of the funnel. The request is therefore the bare add.
//
// ── NO-JS BEHAVIOUR, stated ───────────────────────────────────────────────
// Both buttons are type="button" and are therefore INERT with scripting off.
// That is the deliberate cost of the rule this branch works under — do not
// alter existing add-to-cart behaviour in any way. Making Buy Now a second
// submit button would put it inside hd-product-card.js's `submit` listener,
// which preventDefaults and posts the add itself; the click would then add the
// line twice and still not reach the checkout. A type="button" cannot be the
// form's default submit and cannot fire that listener, so add-to-cart is
// provably untouched. With scripting off the shopper still has the real
// add-to-cart form, which POSTs and lands on /cart — the same floor the PDP has
// had since the port, and the same one the tabs and the tier pills stand on.
(function () {
  'use strict';

  // same revert as assets/hd-offer-bubble.js:72, so the two copy confirmations
  // in this theme hold for the same length of time
  var COPY_MS = 1600;
  // one attribute guards the whole PDP, so init() can run again over an
  // overlapping root without stacking a second set of listeners
  var MARK = 'data-pdpbuy-bound';

  // Shopify's JS routes.root is '/' on the primary locale and '/en/' on the
  // other — WITH the trailing slash, unlike Liquid's routes.root_url. Every
  // URL below is built from it so the shopper never leaves their locale, and
  // the fallback matches hd-product-card.js:44-46 exactly.
  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function copyText(text) {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      try {
        navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        /* fall through to the legacy path */
      }
    }
    try {
      var ta = document.createElement('textarea');
      ta.value = text;
      ta.setAttribute('readonly', '');
      ta.style.position = 'fixed';
      ta.style.top = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      var ok = document.execCommand('copy');
      document.body.removeChild(ta);
      return ok;
    } catch (err) {
      return false;
    }
  }

  // THE ONE ADD. Both controls call this; only `destination` differs.
  //
  // `busy` is a plain in-flight latch, not a disabled state: a second click
  // while the first request is open would add the line twice and then navigate,
  // and the shopper would meet a quantity of 2 at checkout. It is released
  // before the fallback so the native re-post is never blocked by it, and it is
  // deliberately NOT released on success — the page is leaving, and re-arming
  // the button for the half-second before the browser tears the document down
  // is how you get the double add back.
  function wireAdd(form, btn, destination) {
    var busy = false;

    function fallback() {
      busy = false;
      form.submit();
    }

    btn.addEventListener('click', function () {
      if (busy) return;
      busy = true;

      // the try wraps the SYNCHRONOUS half — no fetch at all, a FormData the
      // engine refuses to build — because a throw there would never reach the
      // .catch below and would leave the click with nothing to show, which is
      // the one outcome this theme's cart code refuses to produce
      try {
        fetch(root() + 'cart/add.js', {
          method: 'POST',
          headers: { Accept: 'application/json' },
          body: new FormData(form)
        })
          .then(function (r) {
            return r.json();
          })
          .then(function (json) {
            // the Ajax cart signals failure with a `status` key on a
            // 200-shaped body, so an HTTP check alone would miss the sold-out
            // case
            if (json && json.status) throw new Error(json.description || json.message);
            // assign(), not replace(): the PDP stays in history, so Back from
            // a checkout the shopper thought better of returns to the product
            window.location.assign(destination());
          })
          .catch(fallback);
      } catch (err) {
        fallback();
      }
    });
  }

  function wireCopy(block) {
    var chip = block.querySelector('[data-offer-chip]');
    var hint = block.querySelector('[data-offer-hint]');
    if (!chip || !hint) return;

    var code = block.getAttribute('data-offer-code') || '';
    var idle = block.getAttribute('data-offer-copy') || hint.textContent;
    var done = block.getAttribute('data-offer-copied') || idle;
    var timer = null;

    chip.addEventListener('click', function () {
      // a failed copy leaves the chip alone rather than claiming «تم النسخ» —
      // the code is on screen and can still be read off it by hand
      if (!copyText(code)) return;
      hint.textContent = done;
      chip.classList.add('is-copied');
      if (timer) clearTimeout(timer);
      timer = setTimeout(function () {
        hint.textContent = idle;
        chip.classList.remove('is-copied');
        timer = null;
      }, COPY_MS);
    });
  }

  function init(node) {
    node = node || document;

    var forms = Array.prototype.slice.call(node.querySelectorAll('[data-pdp="form"]'));
    if (node.matches && node.matches('[data-pdp="form"]')) forms.unshift(node);

    forms.forEach(function (form) {
      if (form.hasAttribute(MARK)) return;
      form.setAttribute(MARK, '');

      var buy = form.querySelector('[data-pdp="buy-now"]');
      if (buy) {
        wireAdd(form, buy, function () {
          return root() + 'checkout';
        });
      }

      var block = form.querySelector('[data-pdp-offer]');
      if (!block) return; // the product is not on offer; the block does not exist

      wireCopy(block);

      var apply = block.querySelector('[data-offer-apply]');
      if (apply) {
        wireAdd(form, apply, function () {
          // /discount/CODE sets the code on the session and forwards to
          // `redirect`, so the shopper arrives at checkout with the discount
          // field already filled instead of being told to paste it.
          // The redirect target is a fixed, pre-encoded %2Fcheckout — it is a
          // path this theme owns, not user input, so encoding it again would
          // only turn the '%' into '%25'. The CODE is owner-typed and IS
          // encoded, so a stray space or slash cannot break the path.
          var code = block.getAttribute('data-offer-code') || '';
          return root() + 'discount/' + encodeURIComponent(code) + '?redirect=%2Fcheckout';
        });
      }
    });
  }

  window.HDPdpBuy = { init: init };

  init();

  // Same lifecycle hook, and for the same measured reason, as
  // hd-product-card.js:218-220: the theme editor swaps a section's markup in
  // without reloading, and a fresh form that nobody re-wired would leave Buy
  // Now inert in the preview. init() is guarded by data-pdpbuy-bound, so an
  // overlapping root is safe.
  document.addEventListener('shopify:section:load', function (e) {
    init(e.target);
  });
})();
